/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import Button from '@mui/material/Button';
import { Typography as Text } from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import File from './File';
import AdvancedSettingsMenu from './AdvancedSettingsMenu';
import FolderLocation from './FolderLocation';

import { useUserQuery } from '../../actions/UserActions';
import { useFolderPrefix } from '../../utils/hooks';
import { useFolderOrganizationQuery } from '../../actions/FolderActions';
import LowPriorityMessage from './LowPriorityMessage';

import DialogHeader from './DialogHeader';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

export default function UploadFile({
  handleClose,
  setUploading,
  uploadFile,
  uploadTranscript,
  uploadError,
  exceedsUploadLimit = false,
  uploadDialog,
}) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    numSpeakers: 0,
    language: 'auto',
    outputFormat: 'transcript',
    transcribeMode: 0,
  });

  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const [files, setFiles] = useState([]);
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [fileProgress, setFileProgress] = useState({});
  const { user } = useUserQuery();
  const { folderPrefix: initialFolderPrefix, isRootFolder } = useFolderPrefix({
    organization: user.workspace,
  });

  const { folders: _folders, isFetching } = useFolderOrganizationQuery({
    organizationId: user?.workspace?.id,
    folderPrefix: initialFolderPrefix,
    onlyEditAccess: true,
  });

  useEffect(() => {
    let transcribeMode =
      user?.workspace?.transcribeModes?.length === 1
        ? user?.workspace?.transcribeModes[0].id
        : 0;

    setSettings((prev) => ({
      ...prev,
      outputFormat: user?.workspace?.output_format,
      transcribeMode,
    }));
  }, [user?.workspace?.output_format, user?.workspace?.transcribeModes]);

  const transcribeModeOptions = useMemo(() => {
    return user?.workspace?.transcribeModes
      ?.map((mode) => ({
        label: mode.name,
        value: mode.id,
      }))
      .concat({ label: t('Ingen kommunkoll'), value: 0 })
      .sort((a, b) => a.value - b.value);
  }, [user?.workspace?.transcribeModes]);

  const folders = _folders?.filter((x) => !x.user_fallback);

  const uploadDone =
    progress === 100 ||
    Object.values(fileProgress).every((p) => p.progress >= 100);

  useEffect(() => {
    setFileProgress({});
    setProgress(0);
  }, [files]);

  useEffect(() => {
    if (uploadDialog?.formData) {
      const file = uploadDialog.formData.get('file');
      setFiles([file]);
      setSettings((prev) => ({
        ...prev,
        name: file.name,
      }));
      setStep(1);
    }
  }, [uploadDialog]);

  useEffect(() => {
    if (step === 2) {
      setUploading(true);
    }
    return () => {
      setUploading(false);
    };
  }, [step, files, setUploading]);

  useEffect(() => {
    if (uploadDone || uploadError) {
      setUploading(false);
    }
  }, [uploadDone, uploadError, setUploading]);

  const handleUpload = useCallback(async () => {
    if (!files?.length || !settings?.language) {
      return;
    }

    if (files?.length <= 1 && !settings.name) return;

    setFileProgress(
      files.reduce((acc, file) => {
        acc[file.name] = { name: file.name, progress: 0 };
        return acc;
      }, {})
    );

    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const form = new FormData();
      form.append('file', file);
      const params = {
        ...settings,
        name: file.name,
        folder_prefix: settings.folderPrefix,
      };

      try {
        const mimeType = file.type;

        if (
          mimeType.startsWith('video') ||
          mimeType.startsWith('audio') ||
          (mimeType === '' && file.name.endsWith('.WMA')) ||
          (mimeType === '' && file.name.endsWith('.m4a'))
        ) {
          await uploadTranscript({
            form,
            ...params,
            onProgress: ({ progress }) => {
              setFileProgress((prev) => {
                return {
                  ...prev,
                  [file.name]: { name: file.name, progress: progress * 95 },
                };
              });
              setProgress((progress * 95) / (files?.length || 1));
            },
          });
        } else {
          await uploadFile({
            form,
            params,
            mimeType,
            onProgress: ({ progress }) => {
              setFileProgress((prev) => {
                return {
                  ...prev,
                  [file.name]: { name: file.name, progress: progress * 95 },
                };
              });
              setProgress((progress * 95) / (files?.length || 1));
            },
          });
        }

        setFileProgress((prev) => {
          return {
            ...prev,
            [file.name]: { name: file.name, progress: 100 },
          };
        });
        setProgress(100);
      } catch (e) {
        setError(e.message);
        return;
      }
    }
  }, [settings, files, uploadFile, uploadTranscript]);

  const handleFileUpload = useCallback((files) => {
    if (files?.length === 1) {
      setSettings((prev) => ({
        ...prev,
        name: files[0].name,
      }));
    }
    setFiles(files);
  }, []);

  useEffect(() => {
    if (settings.folderPrefix || isFetching) return;

    if (folders?.length && isRootFolder) {
      const first = folders[0];
      setSettings((prev) => ({
        ...prev,
        folderPrefix: `${first.folder_prefix}${first.id}`,
      }));
    } else {
      setSettings((prev) => ({
        ...prev,
        folderPrefix: initialFolderPrefix,
      }));
    }
  }, [
    initialFolderPrefix,
    settings.folderPrefix,
    folders,
    settings,
    isFetching,
    isRootFolder,
  ]);

  const uploading = !!Object.values(fileProgress)?.length;

  return (
    <>
      <DialogHeader
        title={t('Ladda upp fil')}
        icon={UploadFileOutlinedIcon}
        onClose={handleClose}
      />
      {warning || error ? (
        <div
          css={css`
            display: ${step !== 2 && (warning || error || files?.length)
              ? 'flex'
              : 'none'};
            flex-direction: row;
            align-items: center;
            padding: 0.5rem;

            border: 1px solid
              ${warning
                ? 'var(--warning-50p)'
                : error
                ? 'var(--error-50p)'
                : files?.length
                ? 'var(--success-12p)'
                : 'transparent'};
            border-radius: 0.5rem;

            background-color: ${warning
              ? 'var(--warning-4p)'
              : error
              ? 'var(--error-4p)'
              : files?.length
              ? 'var(--success-4p)'
              : 'transparent'};
          `}
        >
          {error ? (
            <React.Fragment>
              <CancelIcon color="error" />
              <Text
                variant="body2"
                css={css`
                  margin-left: 0.5rem;
                `}
              >
                {error}
              </Text>
            </React.Fragment>
          ) : warning ? (
            <React.Fragment>
              <WarningAmberOutlinedIcon color="warning" />
              <Text
                variant="body2"
                css={css`
                  margin-left: 0.5rem;
                `}
              >
                {warning}
              </Text>
            </React.Fragment>
          ) : null}
        </div>
      ) : null}
      <div>
        <LowPriorityMessage />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          `}
        >
          <File
            files={files}
            onUpload={handleFileUpload}
            onError={setError}
            onWarning={setWarning}
            disabled={exceedsUploadLimit}
            progress={fileProgress}
          />
          {files?.length ? (
            <Button
              color="primary"
              variant={uploading ? 'outlined' : 'contained'}
              fullWidth
              onClick={uploading && uploadDone ? handleClose : handleUpload}
              disabled={
                exceedsUploadLimit ||
                (uploading &&
                  !Object.values(fileProgress).every(
                    (p) => p.progress >= 100
                  )) ||
                settings?.folderPrefix?.split('/').length <= 2
              }
            >
              {uploading ? t('Stäng fönster') : t('Ladda upp')}
            </Button>
          ) : null}
          {!uploading && (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 0.5rem;
              `}
            >
              <FolderLocation
                currentFolderPrefix={settings.folderPrefix}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, folderPrefix: e }))
                }
                disabled={exceedsUploadLimit}
                asButton
              />
              <AdvancedSettingsMenu
                numSpeakers={settings.numSpeakers}
                language={settings.language}
                setSetting={(key, value) =>
                  setSettings((prev) => ({ ...prev, [key]: value }))
                }
                disabled={exceedsUploadLimit}
                outputFormat={settings.outputFormat}
                transcribeMode={settings.transcribeMode}
                transcribeModeOptions={transcribeModeOptions}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
